import { useState, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import WizardModal from "./WizardModal";
import ThinkingLoader from "@/components/ThinkingLoader";
import { Sparkles, RefreshCw, Check, ArrowLeft, Briefcase, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function ProfileWizard({ experiences, onClose, onSave }) {
  const { t, lang } = useT();
  const [mode, setMode] = useState(null); // "experiences" | "questions"
  const [step, setStep] = useState(1); // 1 mode, 2 questions, 3 proposal
  const [who, setWho] = useState("");
  const [personality, setPersonality] = useState("");
  const [skillsInput, setSkillsInput] = useState("");
  const [proposal, setProposal] = useState("");
  const [generating, setGenerating] = useState(false);

  const generate = useCallback(async (m) => {
    setGenerating(true);
    // Reset any previous proposal so an empty API response doesn't leave
    // the modal showing a stale one (or nothing at all — which is what
    // confuses users: the buttons are enabled but there's nothing to edit).
    setProposal("");
    try {
      const { data } = await api.post("/resumes/ai/profile", {
        mode: m, who, personality, skills_input: skillsInput,
        experiences: m === "experiences" ? experiences : [],
        lang,
      });
      const text = (data?.profile || "").trim();
      if (!text) {
        toast.error(t("wizEmptyResult"));
        return;
      }
      setProposal(text);
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("wizFailed"));
    } finally {
      setGenerating(false);
    }
  }, [who, personality, skillsInput, experiences, lang, t]);

  const pickMode = (m) => {
    setMode(m);
    if (m === "experiences") {
      if (!experiences?.length) { toast.error(t("wizNoExperiences")); return; }
      setStep(3);
      generate(m);
    } else {
      setStep(2);
    }
  };

  const submitQuestions = () => {
    if (!who.trim() && !personality.trim() && !skillsInput.trim()) {
      toast.error(t("wizAnswerOne"));
      return;
    }
    setStep(3);
    generate("questions");
  };

  return (
    <WizardModal testId="profile-wizard" title={t("wizProfileTitle")} onClose={onClose}>
      {step === 1 && (
        <div className="space-y-4">
          <p className="font-serif text-xl italic text-neutral-800">{t("wizProfileMode")}</p>
          <button
            data-testid="profile-wizard-mode-exp"
            onClick={() => pickMode("experiences")}
            className="w-full text-left ed-card p-5 hover:border-neutral-900 transition-colors flex items-start gap-4"
          >
            <Briefcase size={18} strokeWidth={1.5} className="mt-0.5 shrink-0" />
            <span>
              <span className="font-serif text-lg block">{t("wizProfileFromExp")}</span>
              <span className="text-xs font-mono text-neutral-500 block mt-1">{t("wizProfileFromExpHint")}</span>
            </span>
          </button>
          <button
            data-testid="profile-wizard-mode-questions"
            onClick={() => pickMode("questions")}
            className="w-full text-left ed-card p-5 hover:border-neutral-900 transition-colors flex items-start gap-4"
          >
            <MessageSquare size={18} strokeWidth={1.5} className="mt-0.5 shrink-0" />
            <span>
              <span className="font-serif text-lg block">{t("wizProfileQuestions")}</span>
              <span className="text-xs font-mono text-neutral-500 block mt-1">{t("wizProfileQuestionsHint")}</span>
            </span>
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div>
            <label className="ed-label">{t("wizWho")}</label>
            <textarea data-testid="profile-wizard-who" className="ed-input min-h-[5rem]" placeholder={t("wizWhoPh")} value={who} onChange={(e) => setWho(e.target.value)} />
          </div>
          <div>
            <label className="ed-label">{t("wizPersonality")}</label>
            <input data-testid="profile-wizard-personality" className="ed-input" placeholder={t("wizPersonalityPh")} value={personality} onChange={(e) => setPersonality(e.target.value)} />
          </div>
          <div>
            <label className="ed-label">{t("wizSkillsQ")}</label>
            <input data-testid="profile-wizard-skills" className="ed-input" placeholder={t("wizSkillsQPh")} value={skillsInput} onChange={(e) => setSkillsInput(e.target.value)} />
          </div>
          <div className="flex items-center justify-between pt-2">
            <button className="ed-btn ed-btn-outline" onClick={() => setStep(1)}>
              <ArrowLeft size={14} strokeWidth={1.5} /> {t("wizBack")}
            </button>
            <button data-testid="profile-wizard-generate" className="ed-btn" onClick={submitQuestions}>
              <Sparkles size={14} strokeWidth={1.5} /> {t("wizGenerate")}
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          {generating ? (
            <div className="ed-card p-8 text-center">
              <Sparkles size={20} strokeWidth={1.5} className="mx-auto text-neutral-400 animate-pulse" />
              <p className="ed-eyebrow mt-3" data-testid="profile-wizard-generating">{t("wizGenerating")}</p>
              <div className="mt-2 flex justify-center">
                <ThinkingLoader stage="resume" testId="profile-wizard-thinking" />
              </div>
            </div>
          ) : !proposal ? (
            // Empty proposal is a real failure mode (AI returned nothing,
            // or the network hiccuped). Rather than showing the reader an
            // empty textarea with active "keep & save" buttons, we surface
            // an in-modal empty state with a single retry button.
            <div className="ed-card p-8 text-center" data-testid="profile-wizard-empty">
              <Sparkles size={20} strokeWidth={1.5} className="mx-auto text-neutral-400" />
              <p className="ed-eyebrow mt-3">{t("wizEmptyResult")}</p>
              <p className="text-xs text-neutral-500 mt-2 max-w-sm mx-auto">{t("wizEmptyResultHelp")}</p>
              <div className="mt-4 flex items-center justify-center gap-2">
                <button className="ed-btn ed-btn-outline" onClick={() => setStep(mode === "questions" ? 2 : 1)}>
                  <ArrowLeft size={14} strokeWidth={1.5} /> {t("wizBack")}
                </button>
                <button className="ed-btn" onClick={() => generate(mode)}>
                  <RefreshCw size={14} strokeWidth={1.5} /> {t("wizRegenerate")}
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs font-mono text-neutral-500">{t("wizProposalHint")}</p>
              <textarea
                data-testid="profile-wizard-proposal"
                className="ed-input min-h-[14rem] leading-relaxed font-serif text-[15px]"
                value={proposal}
                onChange={(e) => setProposal(e.target.value)}
                placeholder={t("wizProposalPlaceholder")}
              />
              <div className="flex items-center justify-between pt-2">
                <button className="ed-btn ed-btn-outline" onClick={() => setStep(mode === "questions" ? 2 : 1)}>
                  <ArrowLeft size={14} strokeWidth={1.5} /> {t("wizBack")}
                </button>
                <div className="flex gap-2">
                  <button data-testid="profile-wizard-regenerate" className="ed-btn ed-btn-outline" onClick={() => generate(mode)}>
                    <RefreshCw size={14} strokeWidth={1.5} /> {t("wizRegenerate")}
                  </button>
                  <button
                    data-testid="profile-wizard-save"
                    className="ed-btn"
                    onClick={() => onSave(proposal.trim())}
                    disabled={!proposal.trim()}
                  >
                    <Check size={14} strokeWidth={1.5} /> {t("wizKeepSave")}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </WizardModal>
  );
}
